import { useCallback, useMemo, useState } from 'react';
import { Table, Typography, TypographyVariants, TypographyColors } from '@carboledger/ui';
import { CellText, SummaryTiles } from '@/components/common';
import { buildTableLabels } from '@/config/tableLabels';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import { useGetMaterialsQuery } from '@/lib/api/lookupsApi';
import {
  useGetReconciliationSummaryQuery,
  useSavePhysicalBalanceMutation,
} from '../api/inventoryApi';
import { buildSummaryEmptyState, buildSummaryNoResults } from '../constants/inventoryEmptyStates';
import { buildReconciliationColumns } from '../constants/summaryColumns';
import { PhysicalBalanceModal } from './PhysicalBalanceModal';

const getRowId = (row) => row.id;
// The surplus / write-off action is drawn but not wired yet, so its click does nothing.
const NOOP = () => {};
const TOTAL_FIGURES = [
  'availableClosingBalance',
  'reconciledQuantity',
  'adjustedClosingBalance',
  'physicalClosingBalance',
];
// Figma's actions column: the two link buttons side by side.
const ACTIONS_WIDTH = 330;

const EMPTY_STATE_KEYS = {
  titleKey: 'INVENTORY_RECONCILIATION_EMPTY_TITLE',
  descriptionKey: 'INVENTORY_RECONCILIATION_EMPTY_DESCRIPTION',
};

/**
 * Props:
 *   header           the sub-tab strip
 *   dateRange        the From – To range, drawn first in the table's toolbar
 *   filtered         the range differs from the default, so an empty result is "no results"
 *   args             the report's variables; null while the range is invalid
 *   module           the route's module segment, for the save
 *   certificateType  scopes the materials lookup the unit conversion reads
 *   materialId       this material's id, to find its units and density
 *   units            { declaration, working } unit symbols
 *   canSave          the rows are saved sub-periods (one whole period picked), so a balance can be saved
 */
export const ReconciliationSummaryView = ({
  header,
  dateRange,
  filtered,
  args,
  module,
  certificateType,
  materialId,
  units,
  canSave,
}) => {
  const t = useTranslations();
  const { data: report, isFetching } = useGetReconciliationSummaryQuery(args, { skip: !args });
  const { data: materials = EMPTY_LIST } = useGetMaterialsQuery(certificateType);
  const [savePhysicalBalance, { isLoading: isSaving }] = useSavePhysicalBalanceMutation();
  const [balanceRow, setBalanceRow] = useState(null);

  const material = useMemo(
    () => materials.find((item) => item.id === String(materialId)) ?? null,
    [materials, materialId],
  );

  const columns = useMemo(
    () => buildReconciliationColumns(t, { unit: units?.declaration ?? '' }),
    [t, units],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  const emptyState = useMemo(() => buildSummaryEmptyState(t, EMPTY_STATE_KEYS), [t]);
  const noResults = useMemo(() => buildSummaryNoResults(t), [t]);

  const handleOpenBalance = useCallback((row) => setBalanceRow(row), []);
  const handleCloseBalance = useCallback(() => setBalanceRow(null), []);
  const handleSaveBalance = useCallback(
    async (quantity) => {
      const result = await savePhysicalBalance({
        module,
        subPeriodId: balanceRow.subPeriodId,
        mbPeriodDetailId: args.mbPeriodDetailId,
        quantity,
        isUpdate: balanceRow.hasPhysicalBalance,
      });
      if (!result.error) setBalanceRow(null);
    },
    [savePhysicalBalance, module, balanceRow, args],
  );

  const cannotSave = useCallback(() => !canSave, [canSave]);
  const hasBalance = useCallback((row) => row.hasPhysicalBalance, []);
  const hasNoBalance = useCallback((row) => !row.hasPhysicalBalance, []);

  const rowActions = useMemo(
    () => [
      {
        id: 'add-physical-balance',
        label: t('INVENTORY_ACTION_ADD_PHYSICAL_BALANCE'),
        hidden: hasBalance,
        disabled: cannotSave,
        onClick: handleOpenBalance,
      },
      {
        id: 'update-physical-balance',
        label: t('INVENTORY_ACTION_UPDATE_PHYSICAL_BALANCE'),
        hidden: hasNoBalance,
        disabled: cannotSave,
        onClick: handleOpenBalance,
      },
      // TODO: Add surplus / write off wizard (Figma 4196:134051) — waiting on how its entries are dated.
      {
        id: 'add-surplus-write-off',
        label: t('INVENTORY_ACTION_ADD_SURPLUS_WRITE_OFF'),
        disabled: true,
        onClick: NOOP,
      },
    ],
    [t, hasBalance, hasNoBalance, cannotSave, handleOpenBalance],
  );

  // A custom range's rows are month buckets with no saved sub-period behind them to save against.
  const toolbarLeft = useMemo(
    () => (
      <>
        {dateRange}
        {!canSave && (
          <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
            {t('INVENTORY_PHYSICAL_BALANCE_WHOLE_PERIOD')}
          </Typography>
        )}
      </>
    ),
    [dateRange, canSave, t],
  );

  const footerRow = useMemo(
    () =>
      report?.totals
        ? Object.fromEntries(
            TOTAL_FIGURES.map((key) => [key, <CellText key={key}>{report.totals[key]}</CellText>]),
          )
        : undefined,
    [report],
  );

  return (
    <SummaryTiles header={header}>
      <Table
        columns={columns}
        data={report?.rows ?? EMPTY_LIST}
        loading={isFetching}
        getRowId={getRowId}
        rowActions={rowActions}
        actionsHeader={t('FIELD_ACTION')}
        actionsWidth={ACTIONS_WIDTH}
        footerRow={footerRow}
        footerLabel={t('INVENTORY_TOTAL_ROW')}
        toolbarLeft={toolbarLeft}
        filtered={filtered}
        showPagination={false}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />

      <PhysicalBalanceModal
        open={balanceRow != null}
        onClose={handleCloseBalance}
        row={balanceRow}
        units={units}
        material={material}
        isSaving={isSaving}
        onSubmit={handleSaveBalance}
      />
    </SummaryTiles>
  );
};
